import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useCreateCampaign, useUpdateCampaign } from '@/hooks/useCampaigns';
import { useTemplates } from '@/hooks/useTemplates';
import { useLeads } from '@/hooks/useLeads';
import { CampaignResponse } from '@/types/api';

interface CampaignModalV2Props {
  isOpen: boolean;
  onClose: () => void;
  campaign?: CampaignResponse | null;
}

export function CampaignModalV2({ isOpen, onClose, campaign }: CampaignModalV2Props) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    templateId: '00000000-0000-0000-0000-000000000000',
    leadIds: [] as string[],
  });

  const createCampaign = useCreateCampaign();
  const updateCampaign = useUpdateCampaign();
  const { data: templates, isLoading: templatesLoading, error: templatesError } = useTemplates();
  const { data: leads, isLoading: leadsLoading, error: leadsError } = useLeads();

  useEffect(() => {
    if (campaign) {
      setFormData({
        name: campaign.name,
        description: campaign.description || '',
        templateId: campaign.templateId?.toString() || '00000000-0000-0000-0000-000000000000',
        leadIds: campaign.leadIds,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        templateId: '00000000-0000-0000-0000-000000000000',
        leadIds: [],
      });
    }
    setStep(1);
  }, [campaign, isOpen]);

  const handleNext = () => setStep(step + 1);
  const handleBack = () => setStep(step - 1);

  const handleSubmit = async () => {
    try {
      if (campaign) {
        await updateCampaign.mutateAsync({ id: campaign.id, campaign: formData });
      } else {
        await createCampaign.mutateAsync(formData);
      }
      onClose();
    } catch (error) {
      console.error('Erro ao salvar campanha:', error);
    }
  };

  const isStepValid = () => {
    switch (step) {
      case 1: return formData.name.trim() !== '';
      case 2: return formData.templateId !== '00000000-0000-0000-0000-000000000000' || (templates?.length === 0);
      case 3: return formData.leadIds.length > 0;
      default: return true;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {campaign ? 'Editar Campanha' : 'Nova Campanha'} - Passo {step} de 3
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Nome da Campanha</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Digite o nome da campanha"
                />
              </div>
              <div>
                <Label htmlFor="description">Descrição (opcional)</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descreva o objetivo da campanha"
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Selecione um Template</h3>
              <div className="text-sm text-muted-foreground">
                {templatesLoading ? 'Carregando templates...' : 
                 templatesError ? 'Erro ao carregar templates' :
                 `Templates disponíveis: ${templates?.length || 0}`}
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Lista de Templates */}
                <div className="space-y-2">
                  {templates && templates.length > 0 ? (
                    templates.map((template) => (
                      <div 
                        key={template.id}
                        className={`p-3 border rounded cursor-pointer transition-colors ${
                          formData.templateId === template.id 
                            ? 'border-primary bg-primary/5' 
                            : 'hover:bg-muted/50'
                        }`}
                        onClick={() => setFormData({ ...formData, templateId: template.id })}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-medium">{template.name}</h4>
                            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                              {template.content}
                            </p>
                          </div>
                          {template.variables && template.variables.length > 0 && (
                            <div className="ml-2 text-xs bg-muted px-2 py-1 rounded">
                              {template.variables.length} var
                            </div>
                          )}
                        </div>
                        {template.variables && template.variables.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {template.variables.slice(0, 3).map((variable) => (
                              <span key={variable} className="text-xs bg-blue-100 text-blue-800 px-1 py-0.5 rounded">
                                {variable}
                              </span>
                            ))}
                            {template.variables.length > 3 && (
                              <span className="text-xs text-muted-foreground">+{template.variables.length - 3}</span>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="p-4 border rounded">
                      <p>Nenhum template encontrado.</p>
                      <div className="mt-2">
                        <div 
                          className="p-3 border rounded cursor-pointer hover:bg-muted/50"
                          onClick={() => setFormData({ ...formData, templateId: '00000000-0000-0000-0000-000000000000' })}
                        >
                          <h4 className="font-medium">Template Padrão (Temporário)</h4>
                          <p className="text-sm text-muted-foreground">Template temporário para testes</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Preview do Template */}
                <div className="border rounded p-4 bg-muted/20">
                  <h4 className="font-medium mb-3">Preview do Template</h4>
                  {(() => {
                    const selectedTemplate = templates?.find(t => t.id === formData.templateId);
                    if (!selectedTemplate) {
                      return <p className="text-sm text-muted-foreground">Selecione um template para ver o preview</p>;
                    }
                    
                    return (
                      <div className="space-y-3">
                        <div className="bg-white p-3 rounded border">
                          <p className="text-sm whitespace-pre-wrap">{selectedTemplate.content}</p>
                        </div>
                        
                        {selectedTemplate.variables && selectedTemplate.variables.length > 0 && (
                          <div>
                            <p className="text-sm font-medium mb-2">Variáveis necessárias:</p>
                            <div className="flex flex-wrap gap-1">
                              {selectedTemplate.variables.map((variable) => {
                                const isAvailable = ['name', 'waId', 'email'].includes(variable);
                                return (
                                  <span 
                                    key={variable} 
                                    className={`text-xs px-2 py-1 rounded ${
                                      isAvailable 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-yellow-100 text-yellow-800'
                                    }`}
                                  >
                                    {variable} {isAvailable ? '✓' : '?'}
                                  </span>
                                );
                              })}
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                              ✓ = Disponível nos leads | ? = Pode não estar disponível
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">Selecione os Leads</h3>
                <div className="text-sm text-muted-foreground">
                  {leadsLoading ? 'Carregando...' : 
                   leadsError ? 'Erro ao carregar' :
                   `${formData.leadIds.length} de ${leads?.length || 0} selecionados`}
                </div>
              </div>
              
              {leadsLoading ? (
                <div className="p-4 text-center text-muted-foreground">Carregando leads...</div>
              ) : leadsError ? (
                <div className="p-4 text-center text-red-500">Erro ao carregar leads</div>
              ) : leads && leads.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Lista de Leads */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          const allIds = leads.map(lead => lead.id);
                          setFormData({ ...formData, leadIds: allIds });
                        }}
                      >
                        Selecionar Todos
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => setFormData({ ...formData, leadIds: [] })}
                      >
                        Limpar Seleção
                      </Button>
                    </div>
                    
                    <div className="max-h-60 overflow-y-auto border rounded p-3 space-y-2">
                      {leads.map((lead) => (
                        <div key={lead.id} className="flex items-center gap-3 p-2 hover:bg-muted/50 rounded">
                          <input 
                            type="checkbox" 
                            id={`lead-${lead.id}`}
                            checked={formData.leadIds.includes(lead.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({ ...formData, leadIds: [...formData.leadIds, lead.id] });
                              } else {
                                setFormData({ ...formData, leadIds: formData.leadIds.filter(id => id !== lead.id) });
                              }
                            }}
                            className="rounded"
                          />
                          <label htmlFor={`lead-${lead.id}`} className="flex-1 cursor-pointer">
                            <div className="font-medium">{lead.name}</div>
                            <div className="text-sm text-muted-foreground">
                              {lead.waId && <span>WhatsApp: {lead.waId}</span>}
                              {lead.email && <span className="ml-2">Email: {lead.email}</span>}
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Preview da Mensagem */}
                  <div className="border rounded p-4 bg-muted/20">
                    <h4 className="font-medium mb-3">Preview da Mensagem</h4>
                    {(() => {
                      const selectedTemplate = templates?.find(t => t.id === formData.templateId);
                      const firstSelectedLead = leads.find(lead => formData.leadIds.includes(lead.id));
                      
                      if (!selectedTemplate) {
                        return <p className="text-sm text-muted-foreground">Template não selecionado</p>;
                      }
                      
                      if (!firstSelectedLead) {
                        return <p className="text-sm text-muted-foreground">Selecione pelo menos um lead para ver o preview</p>;
                      }
                      
                      // Simular substituição de variáveis
                      let previewContent = selectedTemplate.content;
                      
                      // Substituir variáveis conhecidas
                      const replacements: Record<string, string> = {
                        'name': firstSelectedLead.name || '[nome]',
                        'nome': firstSelectedLead.name || '[nome]',
                        'waId': firstSelectedLead.waId || '[whatsapp]',
                        'whatsapp': firstSelectedLead.waId || '[whatsapp]',
                        'telefone': firstSelectedLead.waId || '[telefone]',
                        'email': firstSelectedLead.email || '[email]'
                      };
                      
                      Object.entries(replacements).forEach(([key, value]) => {
                        const regex = new RegExp(`\\{${key}\\}`, 'gi');
                        previewContent = previewContent.replace(regex, value);
                      });
                      
                      return (
                        <div className="space-y-3">
                          <div className="bg-white p-3 rounded border">
                            <p className="text-sm font-medium text-muted-foreground mb-1">
                              Exemplo para: {firstSelectedLead.name}
                            </p>
                            <p className="text-sm whitespace-pre-wrap">{previewContent}</p>
                          </div>
                          
                          {formData.leadIds.length > 1 && (
                            <p className="text-xs text-muted-foreground">
                              Esta mensagem será personalizada para cada um dos {formData.leadIds.length} leads selecionados
                            </p>
                          )}
                          
                          {selectedTemplate.variables && selectedTemplate.variables.length > 0 && (
                            <div className="text-xs text-muted-foreground">
                              <p className="font-medium">Variáveis que serão substituídas:</p>
                              <div className="mt-1">
                                {selectedTemplate.variables.map(variable => {
                                  const value = replacements[variable] || `[${variable}]`;
                                  return (
                                    <div key={variable} className="flex justify-between">
                                      <span>{variable}:</span>
                                      <span className={value.startsWith('[') ? 'text-yellow-600' : 'text-green-600'}>
                                        {value}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ) : (
                <div className="p-4 text-center text-muted-foreground">
                  Nenhum lead encontrado. Importe leads primeiro.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-between pt-4">
          <Button
            variant="outline"
            onClick={step === 1 ? onClose : handleBack}
          >
            {step === 1 ? 'Cancelar' : 'Voltar'}
          </Button>

          {step < 3 ? (
            <Button onClick={handleNext} disabled={!isStepValid()}>
              Próximo
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!isStepValid() || createCampaign.isPending || updateCampaign.isPending}
            >
              {campaign ? 'Atualizar' : 'Criar'} Campanha
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}